/**
 * Stop the cave from playing to an empty room.
 *
 * The music is a set of looping oscillators, not a `<audio>` element, so nothing stops them
 * on its own. A player who presses home mid-run keeps hearing the drone from their pocket
 * until they come back or kill the app — and inside the Android shell, a rewarded video plays
 * over the top of it, which is both unpleasant and an ad the player can barely hear.
 *
 * `visibilitychange` is the right signal for the general case and it is what browsers give
 * us. It is not sufficient on its own for the ad case — a native ad activity does not always
 * take the WebView out of view — which is why `game/ads/nativeBridge.ts` suspends explicitly
 * as well. The two are idempotent: `suspend()` does nothing to an already-suspended context
 * and `resume()` nothing to a running one, so they cannot fight.
 *
 * Deliberately not a React hook. Nothing renders from it, it must outlive every screen, and
 * a hook would tie the game's audio lifetime to whichever component happened to mount it.
 */
import { sound } from './sound';

/** Idempotent: calling twice must not attach two listeners. */
let attached = false;

export function watchPageVisibility(): void {
  if (attached || typeof document === 'undefined') return;
  attached = true;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) sound.suspend();
    else sound.unsuspend();
  });

  /* `pagehide` covers what `visibilitychange` misses on iOS, where a Safari tab being
     frozen or bfcached does not always report itself as hidden first. */
  window.addEventListener('pagehide', () => { sound.suspend(); });
}
